// src/components/admin/employees/BulkImport.js
import React, { useState } from 'react';
import { Upload, X, Download, CheckCircle, AlertTriangle } from 'lucide-react';

const BulkImport = ({ onClose, onImport }) => {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setFile(e.dataTransfer.files[0]);
        }
    };

    const parseCSV = (text) => {
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
            throw new Error('File must contain at least a header row and one data row');
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const requiredFields = ['first_name', 'last_name', 'email', 'department_id', 'designation', 'joining_date'];

        // Check for required fields
        const missingFields = requiredFields.filter(field => !headers.includes(field));
        if (missingFields.length > 0) {
            throw new Error(`Missing required columns: ${missingFields.join(', ')}`);
        }

        const employees = [];
        const errors = [];

        for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim());
            if (values.length !== headers.length) {
                errors.push(`Row ${i + 1}: Column count mismatch`);
                continue;
            }

            const employee = {};
            headers.forEach((header, index) => {
                employee[header] = values[index];
            });

            // Validate required fields
            const emptyFields = requiredFields.filter(field => !employee[field]);
            if (emptyFields.length > 0) {
                errors.push(`Row ${i + 1}: Empty required fields: ${emptyFields.join(', ')}`);
                continue;
            }

            // Validate email format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(employee.email)) {
                errors.push(`Row ${i + 1}: Invalid email format`);
                continue;
            }

            // Validate date format (YYYY-MM-DD)
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(employee.joining_date)) {
                errors.push(`Row ${i + 1}: Invalid date format (use YYYY-MM-DD)`);
                continue;
            }

            employees.push(employee);
        }

        return { employees, errors };
    };

    const handleSubmit = async () => {
        if (!file) return;

        setUploading(true);
        setUploadResult(null);

        try {
            const text = await file.text();
            const { employees, errors } = parseCSV(text);

            if (errors.length > 0) {
                setUploadResult({
                    success: false,
                    message: `Found ${errors.length} validation errors`,
                    errors: errors.slice(0, 10) // Show first 10 errors
                });
                setUploading(false);
                return;
            }

            if (employees.length === 0) {
                setUploadResult({
                    success: false,
                    message: 'No valid employee records found in the file'
                });
                setUploading(false);
                return;
            }

            // Call the import function with parsed employees
            const result = await onImport(employees);

            setUploadResult({
                success: true,
                message: `Successfully imported ${employees.length} employees`,
                count: employees.length
            });

        } catch (error) {
            setUploadResult({
                success: false,
                message: error.message || 'Failed to process file'
            });
        }

        setUploading(false);
    };

    const downloadTemplate = () => {
        // Create a comprehensive CSV template with all required fields
        const csvContent = "first_name,last_name,email,phone,department_id,designation,joining_date,employment_type,manager_id\n" +
            "John,Doe,john.doe@company.com,1234567890,ENG,Software Engineer,2025-01-15,full-time,\n" +
            "Jane,Smith,jane.smith@company.com,0987654321,HR,HR Manager,2025-01-16,full-time,\n" +
            "Mike,Johnson,mike.johnson@company.com,1122334455,SALES,Sales Executive,2025-01-17,full-time,";

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'employee_import_template.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>

                <div className="relative bg-white rounded-lg max-w-md w-full p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold">Bulk Import Employees</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <button
                            onClick={downloadTemplate}
                            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2"
                        >
                            <Download className="w-4 h-4" />
                            Download CSV Template
                        </button>

                        <div
                            className={`border-2 border-dashed rounded-lg p-6 text-center ${
                                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                            }`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                        >
                            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-600">
                                Drag and drop your CSV file here, or{' '}
                                <label className="text-blue-600 hover:text-blue-700 cursor-pointer">
                                    browse
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept=".csv,.xlsx,.xls"
                                        onChange={(e) => setFile(e.target.files[0])}
                                    />
                                </label>
                            </p>
                            {file && (
                                <p className="mt-2 text-sm text-gray-500">
                                    Selected: {file.name}
                                </p>
                            )}
                        </div>

                        {/* Upload Result */}
                        {uploadResult && (
                            <div className={`rounded-lg p-4 ${
                                uploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                            }`}>
                                <div className="flex items-start gap-2">
                                    {uploadResult.success ? (
                                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                                    ) : (
                                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                                    )}
                                    <div className="flex-1">
                                        <p className={`font-medium ${
                                            uploadResult.success ? 'text-green-800' : 'text-red-800'
                                        }`}>
                                            {uploadResult.message}
                                        </p>
                                        {uploadResult.errors && (
                                            <div className="mt-2">
                                                <p className="text-sm text-red-700 mb-1">Validation errors:</p>
                                                <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
                                                    {uploadResult.errors.map((error, index) => (
                                                        <li key={index}>{error}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                            >
                                {uploadResult?.success ? 'Close' : 'Cancel'}
                            </button>
                            {!uploadResult?.success && (
                                <button
                                    onClick={handleSubmit}
                                    disabled={!file || uploading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {uploading ? 'Importing...' : 'Import'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkImport;